import { create } from 'zustand';
import { setApiToken } from './api';
import type { MeResponse, Role } from './api';

const TOKEN_STORAGE_KEY = 'bbl-session-token';

const readStoredToken = (): string | null => {
  if (typeof window === 'undefined') return null;
  try {
    return window.sessionStorage.getItem(TOKEN_STORAGE_KEY);
  } catch {
    return null;
  }
};

const writeStoredToken = (token: string | null): void => {
  if (typeof window === 'undefined') return;
  try {
    if (token) window.sessionStorage.setItem(TOKEN_STORAGE_KEY, token);
    else window.sessionStorage.removeItem(TOKEN_STORAGE_KEY);
  } catch {
    // sessionStorage may be unavailable (private mode / quotas) — ignore
  }
};

interface AuthState {
  user: MeResponse | null;
  token: string | null;
  status: 'idle' | 'loading' | 'authenticated' | 'unauthenticated';
  setUser: (user: MeResponse | null) => void;
  setToken: (token: string | null) => void;
  setStatus: (status: AuthState['status']) => void;
  hasRole: (...roles: Role[]) => boolean;
  reset: () => void;
}

const initialToken = readStoredToken();
setApiToken(initialToken);

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  token: initialToken,
  status: 'idle',
  setUser: (user) =>
    set({ user, status: user ? 'authenticated' : 'unauthenticated' }),
  setToken: (token) => {
    writeStoredToken(token);
    setApiToken(token);
    set({ token });
  },
  setStatus: (status) => set({ status }),
  hasRole: (...roles) => {
    const user = get().user;
    if (!user) return false;
    return roles.includes(user.primaryRole) || user.roles.some((r) => roles.includes(r));
  },
  reset: () => {
    writeStoredToken(null);
    setApiToken(null);
    set({ user: null, token: null, status: 'unauthenticated' });
  },
}));
